import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { authGuard, roleGuard } from './core/guards/auth.guard';
import { AuthService } from './core/services/auth.service';

// Sends the bare /dashboard URL to the right landing page per role.
// HR -> employee directory; everyone else -> overview.
const dashboardLanding = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const role = (auth.role() ?? '').toLowerCase();
  router.navigate([role === 'hr' ? '/dashboard/hr' : '/dashboard/overview']);
  return false;
};

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'login' },

  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then(m => m.LoginComponent)
  },
  {
    path: 'set-password',
    loadComponent: () =>
      import('./features/auth/set-password/set-password.component').then(m => m.SetPasswordComponent)
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
  },

  // Employee portal — any authenticated user can open their own builder
  {
    path: 'employee',
    canActivate: [authGuard, roleGuard(['employee', 'manager', 'hr', 'admin'])],
    loadComponent: () =>
      import('./features/employee/employee-shell/employee-shell.component')
        .then(m => m.EmployeeShellComponent)
  },

  // Add-keywords table page (tabbed, dependency-aware)
  {
    path: 'employee/add-keywords',
    canActivate: [authGuard, roleGuard(['employee', 'manager', 'hr', 'admin'])],
    loadComponent: () =>
      import('./features/employee/keyword-entry/keyword-entry.component')
        .then(m => m.KeywordEntryComponent)
  },

  // Dashboard — manager/hr/admin only
  {
    path: 'dashboard',
    canActivate: [authGuard, roleGuard(['manager', 'hr', 'admin'])],
    loadComponent: () =>
      import('./features/dashboard/dashboard-shell/dashboard-shell.component')
        .then(m => m.DashboardShellComponent),
    children: [
      // role-aware landing instead of a fixed redirect
      { path: '', pathMatch: 'full', canActivate: [dashboardLanding], children: [] },
      {
        // HR is NOT allowed here — only manager/admin
        path: 'overview',
        canActivate: [roleGuard(['manager', 'admin'])],
        loadComponent: () =>
          import('./features/dashboard/pages/overview/overview.component').then(m => m.OverviewComponent)
      },
      {
        // HR is NOT allowed here — only manager/admin
        path: 'search',
        canActivate: [roleGuard(['manager', 'admin'])],
        loadComponent: () =>
          import('./features/dashboard/pages/search/search.component').then(m => m.SearchComponent)
      },
      {
        // Admin-only PRC adoption analytics
        path: 'analytics',
        canActivate: [roleGuard(['admin'])],
        loadComponent: () =>
          import('./features/dashboard/pages/analytics/analytics.component').then(m => m.AnalyticsComponent)
      },
      {
        // HR is NOT allowed here — only manager/admin
        path: 'verticals',
        canActivate: [roleGuard(['manager', 'admin'])],
        loadComponent: () =>
          import('./features/dashboard/pages/verticals/verticals.component').then(m => m.VerticalsComponent)
      },
      {
        // HR is NOT allowed here — only manager/admin
        path: 'feedback',
        canActivate: [roleGuard(['hr', 'admin'])],
        loadComponent: () =>
          import('./features/dashboard/pages/feedback/feedback.component').then(m => m.FeedbackComponent)
      },
      {
        path: 'keywords',
        canActivate: [roleGuard(['manager', 'admin'])],
        loadComponent: () =>
          import('./features/dashboard/pages/keywords/keywords.component')
            .then(m => m.KeywordsComponent)
      },
      {
        path: 'hr',
        canActivate: [roleGuard(['hr', 'admin'])],
        loadComponent: () =>
          import('./features/dashboard/pages/hr/hr.component').then(m => m.HrComponent)
      },
      {
        path: 'my-profile',
        canActivate: [roleGuard(['manager', 'hr', 'admin'])],
        loadComponent: () =>
          import('./features/dashboard/pages/my-profile/my-profile.component').then(m => m.MyProfileComponent)
      }
    ]
  },

  { path: '**', redirectTo: 'login' }
];